import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Database, RefreshCw, Download, Upload, FileCode, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const CrossDeploymentBackup = ({ user }) => {
  const navigate = useNavigate();
  const [copiedSection, setCopiedSection] = useState(null);

  const copyToClipboard = (text, section) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    toast.success(`${section} copied to clipboard`);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const enconOrange = '#ee7203';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/settings')}
            variant="outline"
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Settings
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold" style={{ color: enconOrange }}>
            Cross Deployment Backup Guide
          </h1>
        </div>

        {/* Introduction */}
        <Card className="border-2" style={{ borderColor: enconOrange }}>
          <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Database className="w-6 h-6" style={{ color: enconOrange }} />
              Data Persistence & Code Deployment
            </CardTitle>
            <CardDescription className="text-base">
              Learn how to safely deploy new code while preserving your production data
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                Good News: Your Data is Safe!
              </h3>
              <p className="text-green-800">
                Your data and code are completely separate. Deploying new code will NOT affect your existing entries, vehicles, users, or any other data.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">📊 Database (MongoDB)</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Lives separately from code</li>
                  <li>• Persists across deployments</li>
                  <li>• All entries & data are safe</li>
                  <li>• Same database connection</li>
                </ul>
              </div>
              <div className="p-4 bg-purple-50 border-2 border-purple-200 rounded-lg">
                <h4 className="font-semibold text-purple-900 mb-2">💻 Code (Application)</h4>
                <ul className="text-sm text-purple-800 space-y-1">
                  <li>• Can be updated anytime</li>
                  <li>• Connects to same database</li>
                  <li>• Data remains untouched</li>
                  <li>• Safe to redeploy often</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Workflow Diagram */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5" style={{ color: enconOrange }} />
              Safe Deployment Workflow
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded">
                <h4 className="font-semibold text-green-900 mb-1">1. Production (Deployed App)</h4>
                <p className="text-sm text-green-800">Users adding entries → Data saved to MongoDB</p>
              </div>
              <div className="text-center text-2xl" style={{ color: enconOrange }}>↓</div>
              <div className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                <h4 className="font-semibold text-blue-900 mb-1">2. Development (Your Workspace)</h4>
                <p className="text-sm text-blue-800">Make code changes → Test features → Build new version</p>
              </div>
              <div className="text-center text-2xl" style={{ color: enconOrange }}>↓</div>
              <div className="p-4 bg-purple-50 border-l-4 border-purple-500 rounded">
                <h4 className="font-semibold text-purple-900 mb-1">3. Deploy New Code</h4>
                <p className="text-sm text-purple-800">New code running → SAME database → ALL previous entries intact</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Method 1: Built-in Backup System */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-600" />
              Method 1: Built-in Backup System (Easiest)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <ol className="list-decimal list-inside space-y-3 text-sm">
              <li className="font-medium">
                Go to <span className="px-2 py-1 bg-gray-100 rounded">Settings → Backup</span> in your deployed app
              </li>
              <li className="font-medium">
                Click <span className="px-2 py-1 bg-gray-100 rounded">Create Physical Backup</span>
              </li>
              <li className="font-medium">
                Download the backup file to your computer
              </li>
              <li className="font-medium">
                In your preview environment, restore using the command below
              </li>
            </ol>

            <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm relative">
              <pre className="overflow-x-auto">mongorestore --drop /path/to/backup/</pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-2 right-2 text-gray-400 hover:text-white"
                onClick={() => copyToClipboard('mongorestore --drop /path/to/backup/', 'Command')}
              >
                {copiedSection === 'Command' ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Method 2: MongoDB Commands */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
            <CardTitle className="flex items-center gap-2">
              <FileCode className="w-5 h-5 text-purple-600" />
              Method 2: MongoDB Commands (Direct)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              If both environments have access to MongoDB, use these commands:
            </p>

            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2 text-sm">Export Production Data:</h4>
                <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm relative">
                  <pre className="overflow-x-auto whitespace-pre-wrap">mongodump --uri="mongodb://localhost:27017/transport_system" --out=/tmp/production_backup</pre>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="absolute top-2 right-2 text-gray-400 hover:text-white"
                    onClick={() => copyToClipboard('mongodump --uri="mongodb://localhost:27017/transport_system" --out=/tmp/production_backup', 'Export Command')}
                  >
                    {copiedSection === 'Export Command' ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2 text-sm">Import to Development:</h4>
                <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm relative">
                  <pre className="overflow-x-auto whitespace-pre-wrap">mongorestore --uri="mongodb://localhost:27017/transport_system" /tmp/production_backup/transport_system</pre>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="absolute top-2 right-2 text-gray-400 hover:text-white"
                    onClick={() => copyToClipboard('mongorestore --uri="mongodb://localhost:27017/transport_system" /tmp/production_backup/transport_system', 'Import Command')}
                  >
                    {copiedSection === 'Import Command' ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2 text-sm">One-Line Copy (Advanced):</h4>
                <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm relative">
                  <pre className="overflow-x-auto whitespace-pre-wrap">mongodump --uri="mongodb://[production-url]:27017/transport_system" --archive | mongorestore --uri="mongodb://localhost:27017/transport_system" --archive</pre>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="absolute top-2 right-2 text-gray-400 hover:text-white"
                    onClick={() => copyToClipboard('mongodump --uri="mongodb://[production-url]:27017/transport_system" --archive | mongorestore --uri="mongodb://localhost:27017/transport_system" --archive', 'One-Line Command')}
                  >
                    {copiedSection === 'One-Line Command' ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Method 3: Python Sync Script */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-600" />
              Method 3: Python Sync Script (Automated)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-gray-600">
              A Python script has been created at <code className="px-2 py-1 bg-gray-100 rounded text-xs">/app/backend/sync_production_data.py</code>
            </p>

            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Before Using:</h4>
              <ol className="text-sm text-yellow-800 list-decimal list-inside space-y-1">
                <li>Edit the script and update <code>PRODUCTION_MONGO_URL</code></li>
                <li>Ensure you have network access to production MongoDB</li>
                <li>Test with a small collection first</li>
              </ol>
            </div>

            <div>
              <h4 className="font-semibold mb-2 text-sm">Run the script:</h4>
              <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm relative">
                <pre className="overflow-x-auto">python /app/backend/sync_production_data.py</pre>
                <Button
                  size="sm"
                  variant="ghost"
                  className="absolute top-2 right-2 text-gray-400 hover:text-white"
                  onClick={() => copyToClipboard('python /app/backend/sync_production_data.py', 'Python Command')}
                >
                  {copiedSection === 'Python Command' ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Check */}
        <Card>
          <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" style={{ color: enconOrange }} />
              Quick Check: Are They Already Synced?
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <p className="text-sm text-gray-600">
              Check if both environments use the same MongoDB:
            </p>

            <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm relative">
              <pre className="overflow-x-auto">cat /app/backend/.env | grep MONGO_URL</pre>
              <Button
                size="sm"
                variant="ghost"
                className="absolute top-2 right-2 text-gray-400 hover:text-white"
                onClick={() => copyToClipboard('cat /app/backend/.env | grep MONGO_URL', 'Check Command')}
              >
                {copiedSection === 'Check Command' ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>If both show:</strong> <code>MONGO_URL=mongodb://localhost:27017</code><br />
                <strong>Then:</strong> They're already using the same database! No sync needed.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Important Notes */}
        <Card className="border-2 border-red-200">
          <CardHeader className="bg-red-50">
            <CardTitle className="flex items-center gap-2 text-red-900">
              ⚠️ Important Safety Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <ul className="space-y-2 text-sm text-red-800">
              <li className="flex items-start gap-2">
                <span className="font-bold">•</span>
                <span>Always create backups before syncing data</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold">•</span>
                <span>The <code>--drop</code> flag deletes existing data before restoring</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold">•</span>
                <span>Test the sync process with a copy first</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold">•</span>
                <span>Deploying new code does NOT require data sync - they use the same database</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold">•</span>
                <span>Data sync is only needed when copying between different database instances</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="flex justify-center pb-8">
          <Button
            onClick={() => navigate('/settings')}
            className="gap-2"
            style={{ backgroundColor: enconOrange }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CrossDeploymentBackup;
